import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  useChatRooms,
  useCreateRoom,
  useMarkRoomRead,
  useRoomMessages,
  useSendChatMessage,
  useSendChatMessageWithAttachments,
} from "@/hooks/use-chat";
import { useWebSocket } from "@/hooks/use-websocket";
import * as userApi from "@/api/user.api";
import { listStaffPresence, type ChatRoom, type StaffPresence } from "@/api/chat.api";
import { ChatControllerContext } from "@/components/chat-widget/chat-context";
import type { ChatControllerValue } from "@/components/chat-widget/chat-controller-types";

export type { ChatStaffMember, ChatControllerValue } from "@/components/chat-widget/chat-controller-types";

export function ChatControllerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { connected, send, on } = useWebSocket();
  const { data: rooms = [], isLoading: roomsLoading } = useChatRooms(
    connected ? false : 60_000,
  );
  const [activeRoomId, setActiveRoomId] = useState<string>();
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [presenceByUserId, setPresenceByUserId] = useState<Record<string, StaffPresence>>({});
  const messagesQuery = useRoomMessages(
    activeRoomId,
    { per_page: 50 },
    connected ? false : 60_000,
  );
  const sendMutation = useSendChatMessage();
  const sendAttachmentMutation = useSendChatMessageWithAttachments();
  const createMutation = useCreateRoom();
  const markReadMutation = useMarkRoomRead();
  const staffQuery = useQuery({
    queryKey: ["chat", "team-users"],
    queryFn: async () => {
      const response = await userApi.listUsers({ page: 1, per_page: 200 });
      return (response.data ?? [])
        .filter((staffUser) => staffUser.id !== user?.id)
        .map((staffUser) => ({
          id: staffUser.id,
          name:
            [staffUser.first_name, staffUser.last_name].filter(Boolean).join(" ").trim() ||
            staffUser.email ||
            "Staff member",
          email: staffUser.email,
        }));
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (activeRoomId && !rooms.some((room) => room.id === activeRoomId)) {
      setActiveRoomId(undefined);
    }
  }, [activeRoomId, rooms]);

  useEffect(() => {
    if (!activeRoomId) return;
    markReadMutation.mutate(activeRoomId);
  // Mark once when the viewed room or visible message count changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId, messagesQuery.data?.data.length]);

  useEffect(() => {
    const unsubscribeMessage = on("chat_message", (event) => {
      const payload = event.payload as { room_id?: string };
      if (!payload.room_id) return;
      queryClient.invalidateQueries({ queryKey: ["chat", "rooms"] });
      queryClient.invalidateQueries({ queryKey: ["chat", "messages", payload.room_id] });
    });
    const unsubscribeTyping = on("chat_typing", (event) => {
      const payload = event.payload as {
        room_id?: string;
        user_id?: string;
        typing?: boolean;
      };
      if (
        payload.room_id !== activeRoomId ||
        !payload.user_id ||
        payload.user_id === user?.id
      ) {
        return;
      }
      setTypingUsers((current) =>
        payload.typing
          ? current.includes(payload.user_id!)
            ? current
            : [...current, payload.user_id!]
          : current.filter((id) => id !== payload.user_id),
      );
    });
    const unsubscribeRead = on("chat_read", (event) => {
      const payload = event.payload as {
        room_id?: string;
        user_id?: string;
        read_at?: string;
      };
      if (!payload.room_id || !payload.user_id || !payload.read_at) return;
      const { room_id: roomId, user_id: readerId, read_at: readAt } = payload;
      queryClient.setQueryData<ChatRoom[]>(["chat", "rooms"], (current = []) =>
        current.map((room) =>
          room.id !== roomId
            ? room
            : {
                ...room,
                members: room.members.map((member) =>
                  member.user_id === readerId
                    ? { ...member, last_read_at: readAt }
                    : member,
                ),
              },
        ),
      );
    });
    const unsubscribeSecureMessage = on("new_message", () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "unread"] });
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["outstanding-items"] });
    });
    return () => {
      unsubscribeMessage();
      unsubscribeTyping();
      unsubscribeRead();
      unsubscribeSecureMessage();
    };
  }, [activeRoomId, on, queryClient, user?.id]);

  useEffect(() => setTypingUsers([]), [activeRoomId]);

  useEffect(() => {
    if (!user) {
      setPresenceByUserId({});
      return;
    }
    void listStaffPresence()
      .then((users) => {
        setPresenceByUserId(
          Object.fromEntries(users.map((entry) => [entry.user_id, entry])),
        );
      })
      .catch(() => {
        // Presence is optional - chat still works without it.
      });
  }, [user?.id]);

  useEffect(() => {
    const unsubscribe = on("presence", (event) => {
      const payload = event.payload as StaffPresence;
      if (!payload?.user_id) return;
      setPresenceByUserId((current) => ({
        ...current,
        [payload.user_id]: payload,
      }));
    });
    return unsubscribe;
  }, [on]);

  const roomTitle = useCallback(
    (room?: ChatRoom) => {
      if (!room) return "Staff chat";
      if (room.room_type === "staff_direct") {
        const other = room.members.find(
          (member) => member.user_id && member.user_id !== user?.id,
        );
        return (
          staffQuery.data?.find((staffMember) => staffMember.id === other?.user_id)?.name ??
          "Direct message"
        );
      }
      return room.name ?? (room.room_type === "staff_group" ? "Group chat" : "Chat room");
    },
    [staffQuery.data, user?.id],
  );

  const value = useMemo<ChatControllerValue>(
    () => ({
      rooms,
      roomsLoading,
      activeRoomId,
      activeRoom: rooms.find((room) => room.id === activeRoomId),
      setActiveRoomId,
      messages: messagesQuery.data?.data ?? [],
      messagesLoading: messagesQuery.isLoading,
      staff: staffQuery.data ?? [],
      staffLoading: staffQuery.isLoading,
      typingUsers,
      connected,
      sending: sendMutation.isPending || sendAttachmentMutation.isPending,
      creating: createMutation.isPending,
      totalUnread: rooms.reduce((total, room) => total + (room.unread_count ?? 0), 0),
      presenceByUserId,
      sendMessage: async (body, files = [], onProgress) => {
        if (!activeRoomId) return;
        if (files.length > 0) {
          await sendAttachmentMutation.mutateAsync({
            roomId: activeRoomId,
            body,
            files,
            onProgress,
          });
        } else {
          await sendMutation.mutateAsync({ roomId: activeRoomId, body });
        }
      },
      sendTyping: (typing) => {
        if (activeRoomId) {
          send({ type: "chat_typing", payload: { room_id: activeRoomId, typing } });
        }
      },
      startDirectConversation: async (userId) => {
        const room = await createMutation.mutateAsync({
          room_type: "staff_direct",
          member_ids: [userId],
        });
        setActiveRoomId(room.id);
        return room;
      },
      roomTitle,
    }),
    [
      activeRoomId,
      connected,
      createMutation,
      messagesQuery.data?.data,
      messagesQuery.isLoading,
      roomTitle,
      rooms,
      roomsLoading,
      send,
      sendAttachmentMutation,
      sendMutation,
      staffQuery.data,
      staffQuery.isLoading,
      typingUsers,
      presenceByUserId,
    ],
  );

  return (
    <ChatControllerContext.Provider value={value}>
      {children}
    </ChatControllerContext.Provider>
  );
}

export { useChatController, useChatControllerOptional } from "@/components/chat-widget/chat-hooks";
